import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetPersonsQuery, useSearchPersonsQuery } from "../../redux/api/personsApi";
import { useGetPersonTypesQuery } from "../../redux/api/referencesApi";
import {
  useGetUnlinkedContactsQuery,
  useAutoMatchContactMutation,
  useAutoCreateAllUnlinkedMutation,
} from "../../redux/api/zendeskContactsApi";
import { 
  Pagination, 
  AutoComplete, 
  Input, 
  Select, 
  Table, 
  Button, 
  Tag, 
  Typography, 
  Alert, 
  Space,
  Card,
  Divider,
  message,
  Modal,
  List,
  Collapse,
} from "antd";
import { SearchOutlined, MailOutlined, PhoneOutlined, EyeOutlined, EditOutlined, UserOutlined, LinkOutlined, ReloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { getErrorMessage } from "../../redux/api/baseApi";

const { Text } = Typography;

const PersonsPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedPersonType, setSelectedPersonType] = useState(null);
  const [showUnlinkedContacts, setShowUnlinkedContacts] = useState(false);

  // Debounce searchQuery -> debouncedSearchQuery (300ms) so the paginated
  // table query doesn't refetch on every keystroke. The AutoComplete dropdown
  // continues to use the immediate searchQuery for instant suggestions.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 whenever the active search term changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Only filter the table once the search is long enough to match the
  // AutoComplete's threshold (1 digit for numeric IDs, 2 chars otherwise).
  const tableSearch = useMemo(() => {
    if (!debouncedSearchQuery) return undefined;
    const minLen = /^\d+$/.test(debouncedSearchQuery) ? 1 : 2;
    return debouncedSearchQuery.length >= minLen ? debouncedSearchQuery : undefined;
  }, [debouncedSearchQuery]);

  const { data, isLoading, error } = useGetPersonsQuery({
    page: currentPage,
    limit: itemsPerPage,
    personTypeId: selectedPersonType,
    search: tableSearch,
  });

  // Get person types for filter
  const { data: personTypes = [], isLoading: personTypesLoading } = useGetPersonTypesQuery();

  // Fetch unlinked contacts
  const {
    data: unlinkedData,
    isLoading: unlinkedLoading,
    refetch: refetchUnlinked,
  } = useGetUnlinkedContactsQuery(undefined, { skip: !showUnlinkedContacts });

  // Auto-match mutation
  const [autoMatchContact] = useAutoMatchContactMutation();

  // Auto-create all unlinked mutation
  const [autoCreateAllUnlinked, { isLoading: autoCreating }] = useAutoCreateAllUnlinkedMutation();

  // Search persons query - allow 1 character for numeric searches (person ID), 2 for text searches
  const isNumericSearch = /^\d+$/.test(searchQuery);
  const minSearchLength = isNumericSearch ? 1 : 2;
  const { data: searchData, isLoading: isSearching } = useSearchPersonsQuery(
    { search: searchQuery, limit: 20 },
    { skip: searchQuery.length < minSearchLength }
  );

  const persons = data?.persons || [];
  const pagination = data?.pagination || { current_page: 1, total_pages: 1, total: 0, per_page: 10 };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePersonTypeFilterChange = (value) => {
    setSelectedPersonType(value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  // Generate AutoComplete options from search results
  const searchOptions = useMemo(() => {
    const isNumericSearch = /^\d+$/.test(searchQuery);
    const minSearchLength = isNumericSearch ? 1 : 2;
    if (!searchData?.persons || searchQuery.length < minSearchLength) {
      return [];
    }
    return searchData.persons.map((person) => ({
      value: `${person.person_id} - ${person.first_name} ${person.last_name}`,
      label: (
        <div>
          <div style={{ fontWeight: 500 }}>#{person.person_id} - {person.first_name} {person.last_name}</div>
          {person.email && (
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{person.email}</div>
          )}
          {person.phone && (
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{person.phone}</div>
          )}
          {person.person_type && (
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{person.person_type}</div>
          )}
        </div>
      ),
      person: person,
    }));
  }, [searchData, searchQuery]);

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleSelect = (value, option) => {
    if (option.person) {
      navigate(`/persons/${option.person.person_id}`);
      setSearchQuery("");
    }
  };

  // Handle auto-match for a contact
  const handleAutoMatch = async (contactId) => {
    try {
      const result = await autoMatchContact(contactId).unwrap();
      if (result.success && result.person_id) {
        message.success(`Matched to person by ${result.matched_by}`);
        refetchUnlinked();
      } else {
        message.warning('No matching person found');
      }
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  // Handle auto-create all unlinked contacts as customers
  const handleAutoCreateAll = async () => {
    try {
      const result = await autoCreateAllUnlinked({ person_type_id: 2 }).unwrap();
      message.success(result.message || 'Auto-create completed');
      refetchUnlinked();
      
      // Show detailed stats
      if (result.stats) {
        Modal.info({
          title: 'Auto-Create Customers Summary',
          width: 700,
          content: (
            <div>
              <p><strong>Total unlinked processed:</strong> {result.stats.total_unlinked}</p>
              <p><strong>Matched by email:</strong> {result.stats.matched_by_email}</p>
              <p><strong>Matched by phone:</strong> {result.stats.matched_by_phone}</p>
              <p><strong>New customers created:</strong> {result.stats.created_new}</p>
              {result.stats.skipped_no_name > 0 && (
                <p><strong>Skipped (no name):</strong> {result.stats.skipped_no_name}</p>
              )}
              {result.stats.errors?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Alert
                    type="warning"
                    title={`${result.stats.errors.length} errors occurred`}
                    style={{ marginBottom: 12 }}
                  />
                  <Collapse
                    items={[
                      {
                        key: 'errors',
                        label: `View ${result.stats.errors.length} error(s)`,
                        children: (
                          <List
                            size="small"
                            bordered
                            dataSource={result.stats.errors}
                            renderItem={(error, index) => (
                              <List.Item>
                                <Text type="danger" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                  {index + 1}. {error}
                                </Text>
                              </List.Item>
                            )}
                            style={{ maxHeight: '300px', overflowY: 'auto' }}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          ),
        });
      }
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  // Unlinked contacts table columns
  const unlinkedColumns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text, record) => (
        <Space>
          {record.first_name || record.last_name ? (
            <span>
              {record.first_name} {record.last_name}
            </span>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Sell ID',
      dataIndex: 'zendesk_sell_contact_id',
      key: 'sell_id',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Support ID',
      dataIndex: 'zendesk_support_id',
      key: 'support_id',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          icon={<LinkOutlined />}
          onClick={() => handleAutoMatch(record.id)}
        >
          Auto-Match
        </Button>
      ),
    },
  ];

  // Table columns configuration
  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      width: '25%',
      render: (_, record) => {
        const displayName = [record.first_name, record.last_name]
          .filter(Boolean)
          .join(' ') || record.email || 'Unknown';
        
        return (
          <div>
            <Text type="secondary" style={{ fontSize: '0.85rem', marginRight: 4 }}>
              #{record.person_id}
            </Text>
            <Button
              type="link"
              icon={<UserOutlined />}
              onClick={() => navigate(`/persons/${record.person_id}`)}
              style={{ padding: 0, height: 'auto' }}
              title={record.email || 'View customer details'}
            >
              <strong>{displayName}</strong>
            </Button>
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'person_type',
      key: 'person_type',
      width: '15%',
      render: (type) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: '40%',
      render: (_, record) => (
        <div style={{ lineHeight: '1.8' }}>
          {record.email ? (
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <MailOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: '0.875rem' }}>{record.email}</Text>
            </div>
          ) : (
            <div>
              <MailOutlined style={{ marginRight: 6, color: '#d9d9d9' }} />
              <Text type="secondary" style={{ fontSize: '0.875rem' }}>No email</Text>
            </div>
          )}
          {record.phone ? (
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <PhoneOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: '0.875rem' }}>{record.phone}</Text>
            </div>
          ) : (
            <div>
              <PhoneOutlined style={{ marginRight: 6, color: '#d9d9d9' }} />
              <Text type="secondary" style={{ fontSize: '0.875rem' }}>No phone</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/persons/${record.person_id}`)}
            title="View"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/persons/edit/${record.person_id}`)}
            title="Edit"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Alert title="Loading customers..." type="info" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        title="Error loading customers"
        description={error?.data?.message || error?.error || 'Failed to load customers'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Typography.Title level={1} style={{ margin: 0 }}>Customers</Typography.Title>
        <Button
          type="primary"
          onClick={() => navigate("/persons/add")}
        >
          Add Customer
        </Button>
      </div>

      {/* Search, Filter, and Items per page selector */}
      <Space orientation="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
        <Space wrap style={{ width: '100%' }}>
          <AutoComplete
            style={{ width: 400, minWidth: 250 }}
            options={searchOptions}
            onSearch={handleSearch}
            onSelect={handleSelect}
            value={searchQuery}
            placeholder="Search by name, email, phone, or ID..."
            allowClear
            notFoundContent={
              isSearching 
                ? "Searching..." 
                : searchQuery.length >= (/^\d+$/.test(searchQuery) ? 1 : 2) 
                  ? "No customers found" 
                  : /^\d+$/.test(searchQuery)
                    ? "Type at least 1 digit to search by ID" 
                    : "Type at least 2 characters to search"
            }
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name, email, phone, or ID..."
              size="large"
            />
          </AutoComplete>
          <Select
            style={{ width: 200 }}
            placeholder="Filter by type"
            allowClear
            value={selectedPersonType}
            onChange={handlePersonTypeFilterChange}
            loading={personTypesLoading}
            size="large"
          >
            {personTypes.map((personType) => (
              <Select.Option key={personType.person_type_id} value={personType.person_type_id}>
                {personType.type_name}
              </Select.Option>
            ))}
          </Select>
          <Space>
            <Text type="secondary">Show:</Text>
            <Select
              value={itemsPerPage}
              onChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
              style={{ width: 80 }}
              size="large"
            >
              <Select.Option value={5}>5</Select.Option>
              <Select.Option value={10}>10</Select.Option>
              <Select.Option value={25}>25</Select.Option>
              <Select.Option value={50}>50</Select.Option>
            </Select>
            <Text type="secondary">customers per page</Text>
          </Space>
        </Space>
      </Space>

      {persons.length === 0 ? (
        <Alert
          title="No customers found"
          description={
            searchQuery || selectedPersonType 
              ? 'No customers match your filters.' 
              : 'No customers available.'
          }
          type="info"
          action={
            <Button size="small" onClick={() => navigate("/persons/add")}>
              Add your first customer
            </Button>
          }
          showIcon
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={persons}
            rowKey="person_id"
            pagination={false}
            size="middle"
            rowClassName={(record) => (!record.is_active ? 'ant-table-row-disabled' : '')}
          />

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div style={{ marginTop: 16 }}>
              <Pagination
                current={pagination.current_page}
                total={pagination.total}
                pageSize={itemsPerPage}
                onChange={handlePageChange}
                hideOnSinglePage={true}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}

      {/* Unlinked Contacts Section */}
      <Divider />
      <Card
        title={
          <Space>
            <ExclamationCircleOutlined />
            <span>Unlinked Zendesk Contacts</span>
            {unlinkedData?.contacts && unlinkedData.contacts.length > 0 && (
              <Tag color="orange">{unlinkedData.contacts.length}</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            {showUnlinkedContacts && (
              <>
                <Button
                  type="primary"
                  icon={<UserOutlined />}
                  onClick={handleAutoCreateAll}
                  loading={autoCreating}
                  disabled={!unlinkedData?.contacts || unlinkedData.contacts.length === 0}
                >
                  Auto-Create Customers
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetchUnlinked()}
                  loading={unlinkedLoading}
                >
                  Refresh
                </Button>
              </>
            )}
            <Button
              onClick={() => setShowUnlinkedContacts(!showUnlinkedContacts)}
            >
              {showUnlinkedContacts ? 'Hide' : 'Show'} Unlinked Contacts
            </Button>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        {showUnlinkedContacts && (
          <>
            {unlinkedLoading ? (
              <Alert title="Loading unlinked contacts..." type="info" />
            ) : unlinkedData?.contacts && unlinkedData.contacts.length > 0 ? (
              <>
                <Alert
                  title="Unlinked Contacts"
                  description="These contacts are not linked to any customer in your system. Click 'Auto-Create Customers' to automatically match them by email/phone or create new customers."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Table
                  columns={unlinkedColumns}
                  dataSource={unlinkedData.contacts}
                  loading={unlinkedLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} unlinked contacts`,
                  }}
                  size="small"
                />
              </>
            ) : (
              <Alert
                title="No unlinked contacts"
                description="All Zendesk contacts are linked to customers in the system."
                type="success"
                showIcon
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default PersonsPage;
